import { NewsPost } from './types'

// Keyed per user: two people sharing a browser must not inherit each other's read state. The key is
// deliberately not cleared by logout — clearing it would resurrect the badge on every sign-in.
const lastReadAtKey = (userId: number) => `t1_news_last_read_at_${userId}`

// Storage can be unavailable outright (Safari private browsing, "block all cookies"), and
// ensureLastReadAtSeeded is called during render. A throw there would take the whole page down, so
// the feature degrades to "no badges" instead.
function readStored(userId: number): string | null {
  try {
    return localStorage.getItem(lastReadAtKey(userId))
  } catch {
    return null
  }
}

function writeStored(userId: number, value: string): boolean {
  try {
    localStorage.setItem(lastReadAtKey(userId), value)
    return true
  } catch {
    return false
  }
}

export function getLastReadAt(userId: number): string | null {
  return readStored(userId)
}

// Seeds "now" the first time a user meets the feature, so the entire back catalogue is not
// reported as unread. Deliberately explicit rather than a side effect of getLastReadAt.
export function ensureLastReadAtSeeded(userId: number): string {
  const stored = readStored(userId)
  if (stored !== null) {
    return stored
  }

  const seeded = new Date().toISOString()
  writeStored(userId, seeded)
  return seeded
}

export function markLastReadNow(userId: number): string {
  const now = new Date().toISOString()
  writeStored(userId, now)
  return now
}

export function isNewsPostUnread(post: NewsPost, lastReadAt: string | null): boolean {
  if (lastReadAt === null) {
    return false
  }

  return Date.parse(post.createdAt) > Date.parse(lastReadAt)
}
